import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { createWorkspace } from "@/features/identity/identity.api";
import {
  type CreateWorkspaceInput,
  createWorkspaceInputSchema,
} from "@/features/identity/schemas/inputs.schema";
import { TextField } from "@/shared/components/form/fields/text-field";
import { FormDialog } from "@/shared/components/form/form-dialog";

const DEFAULTS: CreateWorkspaceInput = { name: "", slug: "" };

export function CreateWorkspaceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const qc = useQueryClient();
  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm<CreateWorkspaceInput>({
    resolver: zodResolver(createWorkspaceInputSchema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      form.reset(DEFAULTS);
      setServerError(null);
    }
  }, [open, form]);

  const create = useMutation({
    mutationFn: createWorkspace,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["auth", "me"] });
      onOpenChange(false);
    },
  });

  async function onSubmit(values: CreateWorkspaceInput) {
    setServerError(null);
    const slug = (values.slug ?? "").trim();
    try {
      await create.mutateAsync({ name: values.name.trim(), slug: slug || undefined });
    } catch (e) {
      setServerError(e instanceof Error ? e.message : "Could not create.");
    }
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      form={form}
      onSubmit={onSubmit}
      title="Create a workspace"
      description="A new workspace lives under your current account. You'll need Account Owner or Admin permission."
      submitLabel="Create workspace"
      submitLoadingLabel="Creating…"
      serverError={serverError}
      size="md"
    >
      <TextField
        control={form.control}
        name="name"
        label="Name"
        autoFocus
        placeholder="Acme Marketing"
      />
      <TextField
        control={form.control}
        name="slug"
        label="Slug (optional)"
        placeholder="acme-marketing"
        hint="Leave blank to auto-generate from the name."
      />
    </FormDialog>
  );
}
